/**
 * 퇴사 예약 모달 (V45)
 *
 * 퇴사 예약 등록 및 날짜 변경에 사용됩니다.
 */

import React, { useState, useEffect } from 'react';
import { X, Calendar, AlertCircle } from 'lucide-react';
import { useModalOverlayClose } from '../../../../shared/hooks/useModalOverlayClose';
import { EmploymentManagementItem } from '../../../../shared/hooks/useEmploymentManagement';

interface TerminationScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (plannedEndDate: string, reason?: string) => Promise<void>;
  item: EmploymentManagementItem | null;
  mode: 'schedule' | 'change';
  isLoading: boolean;
}

export function TerminationScheduleModal({
  isOpen,
  onClose,
  onSubmit,
  item,
  mode,
  isLoading,
}: TerminationScheduleModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen && item) {
      if (mode === 'change' && item.terminationPlannedEndDate) {
        setPlannedEndDate(item.terminationPlannedEndDate);
      } else {
        // 새 예약: 기본값은 계약 종료일 또는 오늘 + 30일
        const defaultDate = item.endDate || getDefaultDate();
        setPlannedEndDate(defaultDate);
      }
      setReason('');
      setError('');
    }
  }, [isOpen, item, mode]);

  // 오늘 + 30일 기본값
  const getDefaultDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  // 오늘 날짜 (최소 선택 가능 날짜)
  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // 제출 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검증
    if (!plannedEndDate) {
      setError('퇴사 예정일을 선택해주세요.');
      return;
    }

    const today = getMinDate();
    if (plannedEndDate < today) {
      setError('퇴사 예정일은 오늘 이후여야 합니다.');
      return;
    }

    // 계약 종료일보다 나중이면 안 됨
    if (item?.endDate && plannedEndDate > item.endDate) {
      setError(`퇴사 예정일은 계약 종료일(${item.endDate}) 이후일 수 없습니다.`);
      return;
    }

    try {
      await onSubmit(plannedEndDate, reason || undefined);
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  if (!isOpen || !item) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      {...overlayProps}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            {mode === 'schedule' ? '퇴사 예약' : '퇴사 예정일 변경'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            {/* 직원 정보 */}
            <div
              style={{
                background: 'var(--color-gray-50)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
                대상 직원
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>{item.employeeName}</div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                {item.orgUnitName} · {item.employmentTypeName}
              </div>
              {item.endDate && (
                <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '2px' }}>
                  계약 종료일: {item.endDate}
                </div>
              )}
              {mode === 'change' && item.terminationPlannedEndDate && (
                <div style={{ fontSize: '13px', color: '#ef4444', marginTop: '2px' }}>
                  현재 퇴사 예정일: {item.terminationPlannedEndDate}
                </div>
              )}
            </div>

            {/* 퇴사 예정일 */}
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                {mode === 'schedule' ? '퇴사 예정일' : '새 퇴사 예정일'} *
              </label>
              <div style={{ position: 'relative' }}>
                <Calendar
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-gray-400)',
                  }}
                />
                <input
                  type="date"
                  value={plannedEndDate}
                  onChange={(e) => setPlannedEndDate(e.target.value)}
                  min={getMinDate()}
                  max={item.endDate || '9999-12-31'}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 40px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* 사유 */}
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                사유 (선택)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="퇴사 사유를 입력해주세요"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '13px',
                }}
              >
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* 안내 메시지 */}
            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#92400e',
              }}
            >
              <div style={{ marginBottom: '4px', fontWeight: 600 }}>퇴사일은 마지막 근무일입니다.</div>
              <div style={{ marginBottom: '4px' }}>퇴사 처리(ERP 접근 차단 · 프로젝트 자동 제외)는 퇴사일 다음날 00:15(KST) 자동 실행됩니다.</div>
              <div>예약 후에도 날짜 변경 또는 취소가 가능합니다.</div>
            </div>
          </div>

          {/* 푸터 */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--color-gray-200)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? '처리 중...' : mode === 'schedule' ? '퇴사 예약' : '날짜 변경'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
